import axios from "axios";
import { clearAuthTokens, getAccessToken, getRefreshToken, setAuthTokens } from "../utils/authStorage";

const axiosInstance = axios.create({
    baseURL: "https://api.sejiwa-my.com",
    headers: {
        "Content-Type": "application/json",
    },
});

// Attach JWT token to every request if available
axiosInstance.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

let refreshTokenRequest = null;

const getRefreshedAccessToken = async () => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    if (!refreshTokenRequest) {
        refreshTokenRequest = axios.post(
            `${axiosInstance.defaults.baseURL}/api/auth/refresh`,
            refreshToken,
            {
                headers: {
                    "Content-Type": "text/plain",
                },
            }
        )
            .then((response) => {
                const responseData = response.data;
                const nextAccessToken = responseData?.token || responseData?.accessToken || responseData;
                const nextRefreshToken = responseData?.refreshToken;

                if (!nextAccessToken || typeof nextAccessToken !== 'string') {
                    throw new Error('Refresh API did not return a valid access token');
                }

                setAuthTokens({
                    accessToken: nextAccessToken,
                    refreshToken: nextRefreshToken,
                });

                return nextAccessToken;
            })
            .finally(() => {
                refreshTokenRequest = null;
            });
    }

    return refreshTokenRequest;
};

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        const requestUrl = originalRequest?.url || '';
        const isRefreshRequest = requestUrl.includes('/api/auth/refresh');
        const isAuthBootstrapRequest = requestUrl.includes('/api/auth/login') || requestUrl.includes('/api/auth/register');

        if (status !== 401 || !originalRequest || originalRequest._retry || isRefreshRequest || isAuthBootstrapRequest) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
            const nextAccessToken = await getRefreshedAccessToken();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${nextAccessToken}`;
            return axiosInstance(originalRequest);
        } catch (refreshError) {
            clearAuthTokens();
            return Promise.reject(refreshError);
        }
    }
);

export default axiosInstance;
